import { useState } from 'react';
import type { Role } from '@gobingebox/shared';
import { cn } from '@/lib/cn';
import { PageHero } from '@/components/PagePlaceholder';
import { CommentsAdmin } from '@/features/admin/CommentsAdmin';
import { Content } from '@/features/admin/Content';
import { Members } from '@/features/admin/Members';
import { NotificationsAdmin } from '@/features/admin/NotificationsAdmin';
import { Overview } from '@/features/admin/Overview';
import { GamesAdmin, ProductsAdmin } from '@/features/admin/ShopAdmin';
import { Submissions } from '@/features/admin/Submissions';
import { useAuth } from '@/features/auth/AuthContext';

type Section = 'overview' | 'members' | 'content' | 'comments' | 'submissions' | 'games' | 'store' | 'notifications';

const SECTIONS: { id: Section; label: string; phase?: string; roles: Role[] }[] = [
  { id: 'overview', label: 'Overview', roles: ['admin', 'owner'] },
  { id: 'members', label: 'Members & access', roles: ['admin', 'owner'] },
  { id: 'content', label: 'Content', roles: ['admin', 'owner', 'contributor'] },
  { id: 'comments', label: 'Comments', roles: ['admin', 'owner'] },
  { id: 'submissions', label: 'Submissions', roles: ['admin', 'owner'] },
  { id: 'games', label: 'Games', roles: ['admin', 'owner'] },
  { id: 'store', label: 'Store', roles: ['admin', 'owner'] },
  { id: 'notifications', label: 'Notifications', roles: ['admin', 'owner'] },
];

export function AdminPage() {
  const { user } = useAuth();
  const role = user?.role ?? 'user';
  const sections = SECTIONS.filter((s) => s.roles.includes(role));
  const [section, setSection] = useState<Section>(sections[0]?.id ?? 'content');

  return (
    <div>
      <PageHero
        eyebrow="Dev Panel"
        title="Studio controls"
        subtitle={
          role === 'contributor'
            ? 'Upload and manage content. (Member and billing controls are admin-only.)'
            : 'Manage access, content, submissions and more.'
        }
      />
      <div className="container-page grid gap-8 pb-16 lg:grid-cols-[200px_1fr]">
        <nav className="flex gap-2 overflow-x-auto lg:flex-col">
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSection(s.id)}
              className={cn(
                'flex shrink-0 items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors',
                section === s.id
                  ? 'bg-surface-raised text-ink'
                  : 'text-ink-muted hover:bg-surface-raised/60 hover:text-ink',
              )}
            >
              {s.label}
              {s.phase && <span className="text-2xs text-ink-faint">{s.phase}</span>}
            </button>
          ))}
        </nav>

        <div>
          {section === 'overview' ? (
            <Overview />
          ) : section === 'members' ? (
            <Members />
          ) : section === 'content' ? (
            <Content />
          ) : section === 'comments' ? (
            <CommentsAdmin />
          ) : section === 'submissions' ? (
            <Submissions />
          ) : section === 'games' ? (
            <GamesAdmin />
          ) : section === 'store' ? (
            <ProductsAdmin />
          ) : section === 'notifications' ? (
            <NotificationsAdmin />
          ) : (
            <div className="card px-6 py-14 text-center">
              <p className="font-display text-lg font-bold text-ink">
                {SECTIONS.find((s) => s.id === section)?.label}
              </p>
              <p className="mt-1 text-sm text-ink-muted">Coming soon.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
